import { queryOptions } from '@tanstack/react-query';
import {
  listPublicApartments,
  getPublicApartment,
  searchApartments,
  type PublicApartment,
  type PublicPhoto,
  type SearchResult,
} from '@/api/generated';
import { partyToSearch, type PartyValue } from '@/lib/party';

// HttpError carries the HTTP status so query consumers can distinguish 404
// (render not-found) from transient failures (render retryable error). The
// generated fetch client resolves with { error, response } rather than
// throwing, so the query fns below normalize into a throw.
export class HttpError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

export const apartmentsQuery = queryOptions({
  queryKey: ['public-apartments'],
  queryFn: async (): Promise<PublicApartment[]> => {
    const { data, error, response } = await listPublicApartments();
    if (error || !data) {
      throw new HttpError(response?.status ?? 0, 'failed to list apartments');
    }
    return data.items;
  },
});

// searchQuery fetches apartments available + priced for a date range. checkin
// and checkout are YYYY-MM-DD strings; `party` filters by composition
// (adults/child_age/infants) — the server-authoritative fit check against
// each apartment's occupancy_max and children/infants house rules (Task 7).
// `party` is required, not optional: every call site now has a real
// PartyValue (partyFromSearch always returns a complete one, defaults
// included), and sending real adults/child_age/infants — never the
// deprecated `guests` scalar — is the whole point of this task. The cache
// key includes every composition component so two different parties for
// the same dates never share a result set.
export function searchQuery(params: { checkin: string; checkout: string; party: PartyValue }) {
  const partyParams = partyToSearch(params.party);
  return queryOptions({
    queryKey: [
      'search',
      params.checkin,
      params.checkout,
      partyParams.adults,
      // Sorted so [5, 9] and [9, 5] — the same party, different entry
      // order — share one cache entry rather than two. The picker only
      // appends/pops (never reorders), so this never happens in practice
      // through the UI, but a hand-edited URL could still produce it.
      partyParams.child_age ? [...partyParams.child_age].sort((a, b) => a - b) : null,
      partyParams.infants ?? null,
    ],
    queryFn: async (): Promise<SearchResult[]> => {
      const { data, error, response } = await searchApartments({
        query: {
          checkin: params.checkin,
          checkout: params.checkout,
          adults: partyParams.adults,
          child_age: partyParams.child_age,
          infants: partyParams.infants,
        },
      });
      if (error || !data) {
        throw new HttpError(response?.status ?? 0, 'failed to search apartments');
      }
      return data.results;
    },
    retry: (failureCount, err) => {
      // 422 = bad dates; don't retry. Transient → retry a couple times.
      if (err instanceof HttpError && err.status === 422) return false;
      return failureCount < 2;
    },
  });
}

export function apartmentQuery(id: string) {
  return queryOptions({
    queryKey: ['public-apartment', id],
    queryFn: async (): Promise<PublicApartment> => {
      const { data, error, response } = await getPublicApartment({
        path: { id },
      });
      if (error || !data) {
        throw new HttpError(
          response?.status ?? 0,
          'failed to load apartment',
        );
      }
      return data;
    },
    retry: (failureCount, err) => {
      // Don't retry a genuine 404 — the apartment doesn't exist / isn't bookable.
      if (err instanceof HttpError && err.status === 404) return false;
      return failureCount < 2;
    },
  });
}

// resolveHouseRules (OH-69 §5.7/§7 criterion 8) accepts house_rules' honest
// wire type directly (string | null | undefined) rather than PublicApartment's
// generated `string | undefined` — @hey-api drops the null arm from this
// nullable OPTIONAL field (the same gap from_price_minor/city have; see
// apartment.$id.tsx's withFromPrice comment for the general shape of the
// bug), even though the server really does send an explicit null. Declaring
// the WIDER type here means every caller — real PublicApartment values (the
// generated type is structurally assignable, since `string | undefined` is a
// subset of `string | null | undefined`) and test fixtures alike — needs no
// cast to pass a genuine null through.
//
// Collapses all three "nothing to show" states — field absent (list
// endpoint), explicit null (operator hasn't set any), or whitespace-only
// text — into a single `null`. Mirrors checkout.go's
// `strings.TrimSpace(houseRulesText) != ""` check byte-for-byte: the guest
// UI's "is there anything to accept/show" decision must never disagree with
// the server's, or the checkout consent copy could claim acceptance of
// house rules the backend decided not to record (or vice versa).
export function resolveHouseRules(apt: { house_rules?: string | null }): string | null {
  const raw = apt.house_rules;
  if (typeof raw !== 'string') return null;
  return raw.trim() === '' ? null : raw;
}

// coverPhoto returns the cover image, falling back to the first by sort order.
export function coverPhoto(apt: PublicApartment) {
  if (apt.photos.length === 0) return undefined;
  const cover = apt.photos.find((p) => p.is_cover);
  if (cover) return cover;
  return [...apt.photos].sort((a, b) => a.sort_order - b.sort_order)[0];
}

// AMENITY_LABELS holds the codes the RULE below gets wrong, and nothing else.
//
// The codes arrive from the OTAs through the AdvanceCM import in their own
// spelling and casing. Sentence case is a rule — lowercase everything after
// the first letter — and it handles most of them: "Free Parking" → "Free
// parking", "Room With A View" → "Room with a view". Writing all 48 out by
// hand instead cost 1.3 KB in the entry chunk (ApartmentCard calls this on the
// home page) and blew the bundle budget, which is the gate telling the truth:
// a table of values that a rule already produces is not knowledge, it is bulk.
//
// What stays here is the genuine exceptions — initialisms the rule would
// lowercase, and words it cannot hyphenate or respell.
//
// A future proper noun ("Nespresso Machine") would be lowercased by the rule
// and needs an entry. That is the known cost of choosing a rule over a table.
//
// This is a DICTIONARY and nothing more. It does not merge codes that overlap
// ("Free parking" and "Paid parking" both appear on apartments that genuinely
// offer both), it does not drop codes that are really location or policy
// ("Downtown", "Pets allowed" — that is how the OTAs classify them), and it
// does not touch anything that states a contract term. The operator removes
// what does not belong, in the admin, at the source. A frontend that silently
// hid rows would make the source harder to fix, not easier, by removing the
// evidence that something is wrong.
//
// DELIBERATELY ABSENT: "Free Cancelation", which one apartment carries. It is
// misspelled AND it states a contract term that contradicts the cancellation
// policy the same guest accepts at checkout (5 days full refund, then 50%,
// no-show nothing — OH-68). Giving it a tidy label would make a wrong claim
// look deliberate. It is coming out at the source; until then it renders
// exactly as it arrived.
const AMENITY_LABELS: Record<string, string> = {
  '24hr Checkin': '24-hour check-in',
  'Inperson Checkin': 'In-person check-in',
  'Late Check Out': 'Late check-out',
  'Non Smoking': 'Non-smoking',
  'Free Wifi': 'Free Wi-Fi',
  'Smart Tv': 'Smart TV',
  Cable: 'Cable TV',
  'Iron Board': 'Ironing board',
};

// orderedPhotos returns an apartment's photos in display order: the cover
// first, then by sort_order. Shared by the carousel, the thumbnails and the
// lightbox so "photo 3 of 24" means the same photo in all three.
export function orderedPhotos(apt: PublicApartment): PublicPhoto[] {
  return [...apt.photos].sort((a, b) => {
    if (a.is_cover !== b.is_cover) return a.is_cover ? -1 : 1;
    return a.sort_order - b.sort_order;
  });
}

// photoAlt describes one photo to someone who cannot see it.
//
// Every photo in prod has alt: null — captions arrive with the photo admin,
// which is a different task. Until then the position is the only true thing we
// can say, and it is worth saying: the old code gave all 34 photos of an
// apartment the identical alt (the apartment's title), which tells a screen
// reader user that they are looking at the same picture 34 times.
export function photoAlt(
  photo: PublicPhoto,
  apartmentTitle: string,
  index: number,
  total: number,
): string {
  if (photo.alt) return photo.alt;
  return `${apartmentTitle} — photo ${index + 1} of ${total}`;
}

// formatAmenity turns an amenity code into the label a guest reads: a known
// exception, or the code in sentence case.
//
// The old fallback only uppercased the FIRST character and left the rest as it
// came, so Title-Cased OTA codes stayed Title-Cased and the list rendered half
// one way and half the other — which reads as scraped, because it was.
//
// i18n of the labels themselves is deferred; the site is English-only today.
export function formatAmenity(code: string): string {
  const known = AMENITY_LABELS[code.trim()];
  if (known) return known;
  const spaced = code.replace(/[_-]+/g, ' ').trim().toLowerCase();
  if (spaced.length === 0) return code;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

